import { Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'

import { GapBox } from 'components/Box'
import Filter from 'components/Filter'
import { CommonFormContextProps, SaveButton, useCommonFormContextProps } from 'components/Form'
import { Link } from 'components/Link'
import Paper from 'components/Paper'
import { TableHeader } from 'components/Table'
import {
  DomainListContext,
  DomainListContextType,
} from 'features/inventory/domain/contexts/DomainListContext'
import { DomainTableForm } from 'features/inventory/domain/forms/DomainTableForm'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { useTableSearchParams } from 'providers/router/hooks/useTableSearchParams'
import { useUrlSearchParams } from 'providers/router/hooks/useUrlSearchParams'
import { ROUTES } from 'routes'

export const DomainList = (): React.JSX.Element => {
  const ability = useAbility()
  const { workspaceId } = useWorkspaceParam()
  const { t } = useTranslation('features/domain')
  const { filters, setFilters } = useTableSearchParams()
  const { urlParams } = useUrlSearchParams()

  const commonContextProps: CommonFormContextProps = useCommonFormContextProps({
    formId: `domain-table-form`,
  })
  const contextProps: DomainListContextType = {
    ...commonContextProps,
    filters,
    setFilters,
  }

  const canCreate: boolean = ability.can('create', 'Domain')
  const canUpdate: boolean = ability.can('update', 'Domain')

  const AddNew: React.JSX.Element = (
    <Link
      to={{
        pathname: generatePath(ROUTES.INVENTORY.DOMAIN.ADD, { workspaceId }),
        search: urlParams,
      }}
    >
      <Button
        data-cy='domain-button-add'
        variant='contained'
      >
        {t('list.add')}
      </Button>
    </Link>
  )

  return (
    <Paper>
      <DomainListContext.Provider value={contextProps}>
        <DomainListContext.Consumer>
          {(ctxProps: DomainListContextType): React.JSX.Element => (
            <>
              <TableHeader
                actions={canCreate ? AddNew : undefined}
                filter={
                  <Filter
                    id='domainsFilter'
                    onChange={ctxProps.setFilters}
                    placeholder={t('list.filter')}
                    values={ctxProps.filters}
                  />
                }
                save={
                  <SaveButton
                    {...ctxProps}
                    can={canUpdate}
                  />
                }
                title={t('list.title')}
              />

              <GapBox />
              <DomainTableForm />
            </>
          )}
        </DomainListContext.Consumer>
      </DomainListContext.Provider>
    </Paper>
  )
}
